import { useCallback, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";

import {
  API_APPROVAL_DASHBOARD_EXPORT,
  API_EMPLOYEE_PROJECT_WISE_TIME_REPORT_EXPORT,
  API_TIMESHEET_FILLING_REPORT_EXPORT,
} from "../../../../../config/Endpoints.js";
import { showError, showSuccess } from "../../../../utils/toast.ts";
import type { ApprovalDashboardFilters } from "../types.ts";

function buildExportParams(
  filters: ApprovalDashboardFilters,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    status: filters.status,
  };
  if (filters.approverId != null) params.approverId = filters.approverId;
  if (filters.employeeId != null) params.employeeId = filters.employeeId;
  if (filters.region) params.region = filters.region;
  if (filters.fromDate) params.fromDate = filters.fromDate;
  if (filters.toDate) params.toDate = filters.toDate;
  return params;
}

const REPORT_REGION: Record<string, string> = {
  INDIA: "CCI_INDIA",
  US: "CCI_US",
  UK: "CCI_UK",
  SQE: "SQE",
  GLOBAL: "GLOBAL",
};

function buildFillingReportParams(
  filters: ApprovalDashboardFilters,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    status: filters.status,
    filled: true,
  };
  if (filters.employeeId != null) params.employeeId = filters.employeeId;
  if (filters.region) params.region = REPORT_REGION[filters.region] ?? filters.region;
  if (filters.fromDate) params.fromDate = filters.fromDate;
  if (filters.toDate) params.toDate = filters.toDate;
  return params;
}

function buildProjectTimeReportParams(
  filters: ApprovalDashboardFilters,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (filters.employeeId != null) params.employeeId = filters.employeeId;
  if (filters.fromDate) params.fromDate = filters.fromDate;
  if (filters.toDate) params.toDate = filters.toDate;
  return params;
}

function messageFromResponseData(data: unknown): string | null {
  if (typeof data === "object" && data !== null && "message" in data) {
    const message = (data as { message?: unknown }).message;
    return typeof message === "string" && message.trim() ? message : null;
  }
  return null;
}

async function getExportErrorMessage(error: unknown): Promise<string> {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : "Export failed.";
  }

  const responseData: unknown = error.response?.data;
  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      if (text.trim()) {
        try {
          const message = messageFromResponseData(JSON.parse(text));
          if (message) return message;
        } catch {
          return text;
        }
      }
    } catch {
      // Use the Axios fallback message when the response blob cannot be read.
    }
  }

  return messageFromResponseData(responseData) || error.message || "Export failed.";
}

export function useExportApprovalDashboard() {
  const [exporting, setExporting] = useState(false);
  const token = Cookies.get("accessToken");

  const downloadExport = useCallback(
    async (
      endpoint: string,
      params: Record<string, string | number | boolean>,
      fileName: string,
    ) => {
      setExporting(true);
      try {
        const res = await axios.get<Blob>(endpoint, {
          params,
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        });

        const blob = new Blob([res.data], {
          type:
            res.data?.type ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        showSuccess("Export downloaded.");
      } catch (err) {
        showError(await getExportErrorMessage(err));
      } finally {
        setExporting(false);
      }
    },
    [token],
  );

  const exportApprovalDashboard = useCallback(
    (filters: ApprovalDashboardFilters) => downloadExport(
      API_APPROVAL_DASHBOARD_EXPORT,
      buildExportParams(filters),
      "Approval_Dashboard.xlsx",
    ),
    [downloadExport],
  );

  const exportTimesheetFillingReport = useCallback(
    (filters: ApprovalDashboardFilters) => downloadExport(
      API_TIMESHEET_FILLING_REPORT_EXPORT,
      buildFillingReportParams(filters),
      "Timesheet_Filling_Report.xlsx",
    ),
    [downloadExport],
  );

  const exportEmployeeProjectTimeReport = useCallback(
    (filters: ApprovalDashboardFilters) => {
      if (filters.employeeId == null) {
        showError("Please select an employee name.");
        return;
      }

      return downloadExport(
        API_EMPLOYEE_PROJECT_WISE_TIME_REPORT_EXPORT,
        buildProjectTimeReportParams(filters),
        "Employee_Project_Hours_Report.xlsx",
      );
    },
    [downloadExport],
  );

  return {
    exportApprovalDashboard,
    exportTimesheetFillingReport,
    exportEmployeeProjectTimeReport,
    exporting,
  };
}
